/**
 * idle-prewarm — schedule a one-shot background prewarm for a heavy resource.
 *
 * CL-140 (BUG-050): DuckDB-WASM (~33 MB) must not download at page load, but
 * analytics users should still usually find it warm by the time they ask an
 * analytic question. This helper fires `fn` exactly once on the FIRST of:
 *
 *   (a) any user interaction (pointerdown / keydown / touchstart) — a student
 *       who is about to type or speak a command is a likely analytics user;
 *   (b) browser idle via requestIdleCallback with a timeout fallback
 *       (plain setTimeout where rIC is unavailable, e.g. Safari);
 *   (c) a manual `.trigger()` call (on-demand need beat the scheduler).
 *
 * Guarantees:
 *   - idempotent: `fn` runs at most once, no matter how many triggers race;
 *   - cancellable: `.cancel()` removes all listeners/timers; a cancelled
 *     scheduler never fires (unless it already had);
 *   - self-cleaning: firing removes every listener and pending timer.
 *
 * `fn` itself should be promise-memoized (e.g. ensureDuckDB) so a later
 * on-demand consumer joins the same in-flight work instead of duplicating it.
 *
 * All environment hooks are injectable for unit testing with fake timers.
 */

export const DEFAULT_PREWARM_EVENTS = ['pointerdown', 'keydown', 'touchstart']

/**
 * @param {Function} fn — the prewarm starter; called exactly once
 * @param {object} [opts]
 * @param {string[]} [opts.events] — interaction events that trigger the prewarm
 * @param {number} [opts.idleTimeoutMs] — max wait before firing anyway (default 12000)
 * @param {EventTarget|null} [opts.target] — listener target (default window)
 * @param {Function|null} [opts.requestIdle] — requestIdleCallback override
 * @param {Function|null} [opts.cancelIdle] — cancelIdleCallback override
 * @returns {{ trigger: Function, cancel: Function, fired: () => boolean }}
 */
export function schedulePrewarm(fn, opts = {}) {
  const {
    events = DEFAULT_PREWARM_EVENTS,
    idleTimeoutMs = 12000,
    target = typeof window !== 'undefined' ? window : null,
    requestIdle = typeof requestIdleCallback === 'function' ? requestIdleCallback : null,
    cancelIdle = typeof cancelIdleCallback === 'function' ? cancelIdleCallback : null,
  } = opts

  let settled = false // fired OR cancelled — no future trigger possible
  let fired = false
  let idleId = null
  let timerId = null
  const listeners = []

  const cleanup = () => {
    for (const [ev, handler] of listeners) {
      target?.removeEventListener(ev, handler)
    }
    listeners.length = 0
    if (idleId != null && cancelIdle) cancelIdle(idleId)
    if (timerId != null) clearTimeout(timerId)
    idleId = null
    timerId = null
  }

  const trigger = () => {
    if (settled) return
    settled = true
    fired = true
    cleanup()
    fn()
  }

  if (target?.addEventListener) {
    for (const ev of events) {
      const handler = () => trigger()
      listeners.push([ev, handler])
      target.addEventListener(ev, handler, { once: true, passive: true })
    }
  }

  if (requestIdle) {
    idleId = requestIdle(() => trigger(), { timeout: idleTimeoutMs })
  } else {
    timerId = setTimeout(trigger, idleTimeoutMs)
  }

  return {
    trigger,
    cancel() {
      if (settled) return
      settled = true
      cleanup()
    },
    fired: () => fired,
  }
}
